import React, { useCallback, useEffect, useState } from "react";
import { Eye, FileUp, Pencil, ShieldCheck, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Spinner } from "../common";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { PoliciesManager } from "./PoliciesManager";

/**
 * Phase 218 — Super Admin → Tenant Config → "Policies" tab for the selected municipality.
 *  • Five permission switches for the municipality's Municipal Admins (all OFF by default = menu hidden).
 *  • The full policy library with every right (upload, edit, assign, delete, restore, permanent delete).
 */
const SWITCHES = [
  { key: "view", label: "View", icon: Eye, help: "Shows the Policies menu, the library, version history and the Sign-off report (read-only)." },
  { key: "upload", label: "Upload", icon: FileUp, help: "Upload new policies and new versions of existing ones (implies View)." },
  { key: "edit", label: "Edit", icon: Pencil, help: "Change title, description and effective date (everyone selected must sign again)." },
  { key: "assign", label: "Assign", icon: Users, help: "Choose who must sign: all staff, roles, individuals or a mix." },
  { key: "full", label: "Full Access", icon: ShieldCheck, help: "Everything above plus Delete (to Deleted Policies) and Restore. Permanent delete stays with you." },
];

export function SuperAdminPoliciesPanel({ code, testIdPrefix = "sa-policies" }) {
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => {
    if (!code) return;
    api.get(`/platform/municipalities/${code}/policy-access`).then((r) => setMeta(r.data)).catch((e) => { toast.error(errMsg(e)); setMeta({ access: {}, counts: {} }); });
  }, [code]);
  useEffect(() => { setMeta(null); load(); }, [load]);

  const flip = async (key, value) => {
    setSaving(key);
    try {
      const r = await api.put(`/platform/municipalities/${code}/policy-access`, { [key]: value });
      setMeta((m) => ({ ...(m || {}), access: r.data.access, effective: r.data.effective, updated_at: r.data.updated_at, updated_by: r.data.updated_by }));
      toast.success(`${SWITCHES.find((s) => s.key === key)?.label} ${value ? "granted to" : "removed from"} ${code.toUpperCase()} Municipal Admins`);
      setTick((t) => t + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(null); }
  };

  if (!meta) return <Spinner />;
  const access = meta.access || {};
  const anyOn = Object.values(access).some(Boolean);
  const saRights = { can_view: true, can_upload: true, can_edit: true, can_assign: true, can_delete: true, can_permanent_delete: true, is_super_admin: true };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2"><KeyRound className="h-4 w-4" /> Municipal Admin permissions for Policies</CardTitle>
          <CardDescription>
            What the Municipal Admins of {meta.municipality?.name || code.toUpperCase()} may do in their Policies menu. With every switch OFF the menu is hidden for them.
            {meta.updated_at && <span className="block mt-1 text-xs" data-testid={`${testIdPrefix}-updated`}>Last changed {fmtDateTime(meta.updated_at)} by {meta.updated_by}</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {SWITCHES.map((s) => (
            <div key={s.key} className={`rounded-lg border p-3 flex flex-col gap-2 ${access[s.key] ? "border-primary/40 bg-primary/5" : ""}`} data-testid={`${testIdPrefix}-switch-card-${s.key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-medium"><s.icon className="h-4 w-4" /> {s.label}</span>
                <Switch checked={!!access[s.key]} disabled={saving === s.key} onCheckedChange={(v) => flip(s.key, v)} data-testid={`${testIdPrefix}-switch-${s.key}`} aria-label={`${s.label} permission`} />
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">{s.help}</p>
              <Badge variant={access[s.key] ? "default" : "outline"} className="w-fit text-[10px]" data-testid={`${testIdPrefix}-state-${s.key}`}>{access[s.key] ? "ON" : "OFF"}</Badge>
            </div>
          ))}
        </CardContent>
        <CardContent className="pt-0 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span data-testid={`${testIdPrefix}-menu-state`}>Policies menu for Municipal Admins: <b className={anyOn ? "text-emerald-700" : "text-amber-700"}>{anyOn ? "visible" : "hidden"}</b></span>
          <span>•</span>
          <span data-testid={`${testIdPrefix}-counts`}>{meta.counts?.active ?? 0} active • {meta.counts?.deleted ?? 0} deleted • {meta.counts?.signatures ?? 0} signatures • {meta.counts?.staff ?? 0} staff accounts</span>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Policy library — {meta.municipality?.name || code.toUpperCase()}</CardTitle>
          <CardDescription>You have every right here: upload, edit, choose who must sign, delete, restore and permanently delete.</CardDescription>
        </CardHeader>
        <CardContent>
          <PoliciesManager key={`${code}-${tick}`} code={code} rights={saRights} onChanged={load} testIdPrefix={testIdPrefix} />
        </CardContent>
      </Card>
    </div>
  );
}
